import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  experienceStorage,
  STORAGE_KEYS,
} from '@experiences/blocs/utils/storage';
import {
  LocalActionCounts,
  reduceUnsyncedActionCounts,
  serializeActionEntry,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/count-actions';
import {
  popSnapshot,
  pushSnapshot,
} from '@/experiences/sales-portal/blocs/utils/snapshot-undo-stack';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import {
  RecommendationAction,
  RecommendationActionsSlice,
  RecommendationActionsSnapshot,
  RecommendationActionsStore,
} from './recommendation-actions-store-types';

export const useRecommendationActionsStore =
  create<RecommendationActionsStore>()(
    persist(
      (set) => ({
        acceptRecommendation: (
          uuid,
          restaurantUUID,
          variantPackUUID,
          supplierUUID,
        ) =>
          set((state) => {
            pricingManagementAnalytics.track(
              PricingAnalyticEvent.AGENT_ACCEPT,
              {
                recommendationUUID: uuid,
                restaurantUUID,
                supplierUUID,
                variantPackUUID,
              },
            );
            return {
              actions: {
                ...state.actions,
                [uuid]: {
                  action: RecommendationAction.Accepted,
                  recommendationUUID: uuid,
                  supplierUUID,
                },
              },
              actionSnapshotStack: pushSnapshot(
                state.actionSnapshotStack,
                state.actions,
              ),
            };
          }),

        actions: {},

        actionSnapshotStack: [],

        actionsResetEpoch: 0,

        activeTaskTypeId: null,

        clearAllActions: () =>
          set((state) => ({
            actions: {},
            actionSnapshotStack: pushSnapshot(
              state.actionSnapshotStack,
              state.actions,
            ),
            actionsResetEpoch: state.actionsResetEpoch + 1,
            rejectedReasonHistoryMap: {},
          })),

        clearOverride: (uuid) =>
          set((state) => {
            if (!state.actions[uuid]) {
              return state;
            }
            const next = { ...state.actions };
            delete next[uuid];
            return {
              actions: next,
              actionSnapshotStack: pushSnapshot(
                state.actionSnapshotStack,
                state.actions,
              ),
            };
          }),

        cycleAction: (uuid, restaurantUUID, variantPackUUID, supplierUUID) =>
          set((state) => {
            const current = state.actions[uuid];
            if (!current) {
              pricingManagementAnalytics.track(
                PricingAnalyticEvent.AGENT_ACCEPT,
                {
                  recommendationUUID: uuid,
                  restaurantUUID,
                  supplierUUID,
                  variantPackUUID,
                },
              );
              return {
                actions: {
                  ...state.actions,
                  [uuid]: {
                    action: RecommendationAction.Accepted,
                    recommendationUUID: uuid,
                    supplierUUID,
                  },
                },
                actionSnapshotStack: pushSnapshot(
                  state.actionSnapshotStack,
                  state.actions,
                ),
              };
            }

            if (current.action === RecommendationAction.Accepted) {
              const historicReason = state.rejectedReasonHistoryMap[uuid];
              pricingManagementAnalytics.track(
                PricingAnalyticEvent.AGENT_REJECT,
                {
                  recommendationUUID: uuid,
                  restaurantUUID,
                  supplierUUID,
                  variantPackUUID,
                },
              );
              return {
                actions: {
                  ...state.actions,
                  [uuid]: {
                    action: RecommendationAction.Rejected,
                    recommendationUUID: uuid,
                    rejectionReason: historicReason ?? current.rejectionReason,
                    supplierUUID,
                  },
                },
                actionSnapshotStack: pushSnapshot(
                  state.actionSnapshotStack,
                  state.actions,
                ),
              };
            }

            pricingManagementAnalytics.track(PricingAnalyticEvent.AGENT_RESET, {
              recommendationUUID: uuid,
              restaurantUUID,
              supplierUUID,
              variantPackUUID,
            });
            const next = { ...state.actions };
            delete next[uuid];
            return {
              actions: next,
              actionSnapshotStack: pushSnapshot(
                state.actionSnapshotStack,
                state.actions,
              ),
            };
          }),

        markActionsSynced: (entries) =>
          set((state) => {
            const next = { ...state.actions };
            let changed = false;
            for (const entry of entries) {
              const current = next[entry.recommendationUUID];
              const signature = serializeActionEntry(entry);
              // An edit made while the request was in flight is still unsaved,
              // so only mark the entry that was actually sent.
              if (!current || serializeActionEntry(current) !== signature) {
                continue;
              }
              next[entry.recommendationUUID] = {
                ...current,
                syncedSignature: signature,
              };
              changed = true;
            }
            return changed ? { actions: next } : state;
          }),

        pruneStaleRecommendations: (validRecommendationUUIDs) =>
          set((state) => {
            const valid = new Set(validRecommendationUUIDs);
            let changed = false;
            const nextActions: RecommendationActionsSnapshot = {};
            for (const [uuid, entry] of Object.entries(state.actions)) {
              if (valid.has(uuid)) {
                nextActions[uuid] = entry;
              } else {
                changed = true;
              }
            }
            if (!changed) {
              return state;
            }
            return {
              actions: nextActions,
              actionSnapshotStack: [],
              actionsResetEpoch: state.actionsResetEpoch + 1,
            };
          }),

        pruneStaleTaskTypes: (validTaskTypeIds) =>
          set((state) => {
            const valid = new Set(validTaskTypeIds);
            const nextSlices: Record<string, RecommendationActionsSlice> = {};
            for (const [id, slice] of Object.entries(state.slicesByType)) {
              if (valid.has(id)) {
                nextSlices[id] = slice;
              }
            }
            const activeStale =
              state.activeTaskTypeId !== null &&
              !valid.has(state.activeTaskTypeId);
            return {
              actions: activeStale ? {} : state.actions,
              actionSnapshotStack: activeStale ? [] : state.actionSnapshotStack,
              actionsResetEpoch: activeStale
                ? state.actionsResetEpoch + 1
                : state.actionsResetEpoch,
              activeTaskTypeId: activeStale ? null : state.activeTaskTypeId,
              rejectedReasonHistoryMap: activeStale
                ? {}
                : state.rejectedReasonHistoryMap,
              slicesByType: nextSlices,
            };
          }),

        rejectedReasonHistoryMap: {},

        rejectRecommendation: (
          uuid,
          restaurantUUID,
          variantPackUUID,
          supplierUUID,
        ) =>
          set((state) => {
            const current = state.actions[uuid];
            pricingManagementAnalytics.track(
              PricingAnalyticEvent.AGENT_REJECT,
              {
                recommendationUUID: uuid,
                restaurantUUID,
                supplierUUID,
                variantPackUUID,
              },
            );
            const historicReason = state.rejectedReasonHistoryMap[uuid];
            return {
              actions: {
                ...state.actions,
                [uuid]: {
                  action: RecommendationAction.Rejected,
                  recommendationUUID: uuid,
                  rejectionReason: historicReason ?? current?.rejectionReason,
                  supplierUUID,
                },
              },
              actionSnapshotStack: pushSnapshot(
                state.actionSnapshotStack,
                state.actions,
              ),
            };
          }),

        setOverridePrice: (
          uuid,
          priceInMicros,
          grossProfitInPercentage,
          supplierUUID,
        ) =>
          set((state) => ({
            actions: {
              ...state.actions,
              [uuid]: {
                ...state.actions[uuid],
                action: RecommendationAction.Overridden,
                overrideGrossProfitInPercentage: grossProfitInPercentage,
                overridePriceInMicros: priceInMicros,
                recommendationUUID: uuid,
                supplierUUID,
              },
            },
            actionSnapshotStack: pushSnapshot(
              state.actionSnapshotStack,
              state.actions,
            ),
          })),

        setRejectionReason: (uuid, reason) =>
          set((state) => {
            const current = state.actions[uuid];
            if (!current) {
              return state;
            }
            const nextActions = {
              ...state.actions,
              [uuid]: { ...current, rejectionReason: reason },
            };
            if (current.action === RecommendationAction.Rejected) {
              return {
                actions: nextActions,
                rejectedReasonHistoryMap: {
                  ...state.rejectedReasonHistoryMap,
                  [uuid]: reason,
                },
              };
            }
            return { actions: nextActions };
          }),

        slicesByType: {},

        syncTaskType: (taskTypeId) =>
          set((state) => {
            if (state.activeTaskTypeId === taskTypeId) {
              return state;
            }

            const nextSlices = { ...state.slicesByType };
            if (state.activeTaskTypeId) {
              nextSlices[state.activeTaskTypeId] = {
                actions: state.actions,
                actionSnapshotStack: state.actionSnapshotStack,
                rejectedReasonHistoryMap: state.rejectedReasonHistoryMap,
              };
            }

            const incoming = nextSlices[taskTypeId];
            delete nextSlices[taskTypeId];

            return {
              actions: incoming?.actions ?? {},
              actionSnapshotStack: incoming?.actionSnapshotStack ?? [],
              actionsResetEpoch: state.actionsResetEpoch + 1,
              activeTaskTypeId: taskTypeId,
              rejectedReasonHistoryMap:
                incoming?.rejectedReasonHistoryMap ?? {},
              slicesByType: nextSlices,
            };
          }),

        undo: () =>
          set((state) => {
            const { previous, remaining } = popSnapshot(
              state.actionSnapshotStack,
            );
            if (!previous) {
              return state;
            }
            return {
              actions: previous,
              actionSnapshotStack: remaining,
            };
          }),
      }),
      {
        // Bumped from the legacy single-run shape. With no migrate function, a
        // version mismatch discards the old persisted state and falls back to
        // defaults — the intended behavior for these ephemeral edits.
        name: STORAGE_KEYS.PRICING_AGENT_ACTIONS,
        partialize: (state) => ({
          actions: state.actions,
          activeTaskTypeId: state.activeTaskTypeId,
          rejectedReasonHistoryMap: state.rejectedReasonHistoryMap,
          // Persist edits and reason history per type, but not the undo stacks.
          slicesByType: Object.fromEntries(
            Object.entries(state.slicesByType).map(([id, slice]) => [
              id,
              {
                actions: slice.actions,
                actionSnapshotStack: [],
                rejectedReasonHistoryMap: slice.rejectedReasonHistoryMap,
              },
            ]),
          ),
        }),
        storage: createJSONStorage(() => ({
          getItem: (key) => experienceStorage.getString(key) ?? null,
          removeItem: (key) => experienceStorage.remove(key),
          setItem: (key, value) => experienceStorage.set(key, value),
        })),
        version: 1,
      },
    ),
  );

/**
 * Local unpublished action counts (by type) for a task type. The active type's
 * edits live in the top-level `actions` mirror; inactive types are stashed in
 * `slicesByType`. Decisions the status sync already persisted are excluded —
 * the backend summary counts those.
 */
export const localActionCountsForType = (
  state: Pick<
    RecommendationActionsStore,
    'actions' | 'activeTaskTypeId' | 'slicesByType'
  >,
  taskTypeId: string,
): LocalActionCounts => {
  const slice =
    taskTypeId === state.activeTaskTypeId
      ? state.actions
      : state.slicesByType[taskTypeId]?.actions;
  return reduceUnsyncedActionCounts(slice ?? {});
};
